import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Plus, Lock } from 'lucide-react';
import { listSkills } from '@/services/skillsApi';
import type { TenantSkill, UserSkill } from '@Types/skills';
import { SkillCard } from './SkillCard';
import { SkillsAsyncState } from './SkillsAsyncState';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

interface SkillsLibraryTabProps {
  refreshKey?: number;
  onOpenSkill: (
    skillId: string,
    scope: 'user' | 'tenant',
    readOnly?: boolean,
  ) => void;
  onCreateNew: (scope: 'user' | 'tenant') => void;
}

export const SkillsLibraryTab = ({
  refreshKey,
  onOpenSkill,
  onCreateNew,
}: SkillsLibraryTabProps) => {
  const [userSkills, setUserSkills] = useState<UserSkill[]>([]);
  const [tenantSkills, setTenantSkills] = useState<TenantSkill[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listSkills();
      setUserSkills(res.user);
      setTenantSkills(res.tenant);
      setIsAdmin(!!res.is_admin);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const tenantBadge = (
    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      Tenant
    </span>
  );
  const authoredBadge = (
    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
      Authored
    </span>
  );
  const lockedBadge = (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
      <Lock className="h-3 w-3" />
      Read-only
    </span>
  );

  const userEmpty = userSkills.length === 0;

  return (
    <SkillsAsyncState
      loading={loading}
      error={loadError}
      onRetry={refresh}
      retryLabel="Try again">
      <div className="space-y-6">
        <section className="space-y-3">
          <div className="flex items-end justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-[#252F33]">
                Tenant skills
              </h2>
              <p className="text-xs text-[#768D95]">
                {isAdmin
                  ? 'Org-wide skills you manage. Edits propagate to everyone in your tenant.'
                  : 'Org-wide skills published by your tenant admins. Read-only.'}
              </p>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  trackAdpWaypoint(
                    getBernoulliAdp(),
                    ADP_EVENTS.SKILLS_CREATE_START,
                    {
                      scope: 'tenant',
                    },
                  );
                  onCreateNew('tenant');
                }}
                className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-shrink-0">
                <Plus className="h-4 w-4" />
                New tenant skill
              </button>
            )}
          </div>

          {tenantSkills.length === 0 ? (
            <div className="text-xs text-[#768D95] px-3 py-4 border border-dashed border-slate-200 rounded-lg text-center">
              {isAdmin
                ? 'No tenant skills yet. Create one to share with everyone in your tenant.'
                : 'No tenant skills yet. Ask a tenant admin to publish one.'}
            </div>
          ) : (
            <div className="space-y-3">
              {tenantSkills.map(skill => {
                const editable = isAdmin && skill.is_own_tenant;
                return (
                  <SkillCard
                    key={skill.skill_id}
                    name={skill.name}
                    description={skill.description}
                    badge={
                      <span className="flex items-center gap-1">
                        {tenantBadge}
                        {!editable && lockedBadge}
                      </span>
                    }
                    onClick={() => {
                      trackAdpEvent(
                        getBernoulliAdp(),
                        ADP_EVENTS.SKILLS_OPEN_EDITOR,
                        {
                          skill_id: skill.skill_id,
                          scope: 'tenant',
                          read_only: String(!editable),
                        },
                      );
                      onOpenSkill(skill.skill_id, 'tenant', !editable);
                    }}
                  />
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-end justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-[#252F33]">
                My skills
              </h2>
              <p className="text-xs text-[#768D95]">Skills you authored.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                trackAdpWaypoint(
                  getBernoulliAdp(),
                  ADP_EVENTS.SKILLS_CREATE_START,
                  {
                    scope: 'user',
                  },
                );
                onCreateNew('user');
              }}
              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 text-[#252F33] hover:bg-slate-50 flex-shrink-0">
              <Plus className="h-4 w-4" />
              New skill
            </button>
          </div>

          {userEmpty ? (
            <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-slate-200 rounded-lg">
              <Sparkles className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-sm text-[#252F33] font-medium">
                No skills yet
              </p>
              <p className="text-xs text-[#768D95] mt-1 max-w-xs">
                Create your own skill to inject custom instructions into the
                system prompt.
              </p>
              <button
                type="button"
                onClick={() => {
                  trackAdpWaypoint(
                    getBernoulliAdp(),
                    ADP_EVENTS.SKILLS_CREATE_START,
                    {
                      scope: 'user',
                    },
                  );
                  onCreateNew('user');
                }}
                className="inline-flex items-center gap-1 text-sm font-medium px-4 py-2 mt-4 rounded-md bg-brand-link text-white hover:bg-[#005a94]">
                <Plus className="h-4 w-4" />
                New skill
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {userSkills.map(skill => (
                <SkillCard
                  key={skill.skill_id}
                  name={skill.name}
                  description={skill.description}
                  badge={authoredBadge}
                  onClick={() => {
                    trackAdpEvent(
                      getBernoulliAdp(),
                      ADP_EVENTS.SKILLS_OPEN_EDITOR,
                      {
                        skill_id: skill.skill_id,
                        scope: 'user',
                        read_only: 'false',
                      },
                    );
                    onOpenSkill(skill.skill_id, 'user');
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </SkillsAsyncState>
  );
};
